// PATH: src/configs/axiosInstance.js
import axios from "axios";
import { isLoggedIn, setSession } from "utils/jwt";
import { clearSessionCookies, AUTH_EXPIRED_EVENT } from "utils/session";
import { decryptData } from "./encryption";

// Axios instances
export const instance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/admin`,
});

export const masterInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/master`,
});

export const userInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/user`,
});

// ---------------------------------------------------------------------------
// Global 401 handling.
// Backend answers either HTTP 401 or { code: 401 } inside the (maybe
// encrypted) body when the token is invalid/expired. Previously this was only
// handled for `instance` (admin), which the app never uses - so a dead token
// kept the user "inside". Now every instance clears the session and tells
// AuthProvider (via event) to log out.
// ---------------------------------------------------------------------------
const SKIP_URLS = ["/login", "/register"]; // a 401 here is a failed login, not an expired session

const readCode = (response) => {
  try {
    const raw = response?.data;
    if (raw && typeof raw === "object") return raw.code;
    return decryptData(response)?.code;
  } catch {
    return undefined;
  }
};

const handleUnauthorized = (config) => {
  const url = config?.url || "";
  if (SKIP_URLS.some((u) => url.includes(u))) return;

  clearSessionCookies();
  setSession(null);
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

[instance, masterInstance, userInstance].forEach((inst) => {
  inst.interceptors.response.use(
    (response) => {
      if (readCode(response) === 401) handleUnauthorized(response.config);
      return response;
    },
    (error) => {
      if (error?.response?.status === 401) handleUnauthorized(error.config);
      return Promise.reject(error);
    },
  );
});

export const setJwtToken = () => ({
  headers: {
    Authorization: "Bearer " + isLoggedIn(),
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

export const setMultiPartHeader = () => ({
  headers: {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
  },
});